/**
 * FastByNoFeeBadge — public Fast by NoFee identity badge.
 *
 * iter134 (Jul 2026) — Owner spec: Fast by NoFee is now a
 * TRUST program based on Verified On-Time Delivery. All public
 * tier language (Fast Standard / Plus / Premium / Thunder,
 * Fast Stars, Fast Badges, ranking, progression) is retired.
 * The badge simply signals that the restaurant participates in
 * the Fast by NoFee program — no public comparison between
 * restaurants. Fast Score remains an internal operational
 * metric visible only inside the restaurant dashboard.
 *
 * Props:
 *   fastEnabled   — whether the restaurant participates. Hides
 *                   the badge when false.
 *   variant       — "card" | "hero" | "inline" | "icon"
 *   restaurantName — for the FastInfoModal header
 *
 * Legacy props (tier, tierLabel, points, pointsToNext,
 * nextTier, verifiedDeliveries, averageRating, ratingCount) are
 * accepted as no-ops so existing call sites keep working during
 * the transition — the badge never renders tier data.
 */
import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import FastInfoModal from './FastInfoModal';

const BADGE_LABEL = 'Fast by NoFee';

const FastByNoFeeBadge = ({
  fastEnabled = true,
  variant = 'card',
  className = '',
  testid = 'fast-badge',
  restaurantName,
  // ── Legacy props (accepted, no-op — kept for backwards-compat) ──
  tier,
  tierLabel,
  points = 0,
  pointsToNext = null,
  nextTier = null,
  verifiedDeliveries = 0,
  averageRating = null,
  ratingCount = null,
}) => {
  const [open, setOpen] = useState(false);
  if (!fastEnabled) return null;

  const open_modal = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(true);
  };

  const openModalIfNeeded = open ? (
    <FastInfoModal
      restaurantName={restaurantName}
      onClose={() => setOpen(false)}
    />
  ) : null;

  if (variant === 'hero') {
    return (
      <>
        <button
          type="button"
          onClick={open_modal}
          data-testid={`${testid}-hero`}
          className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 backdrop-blur transition-transform hover:scale-[1.02] bg-black/85 text-white border-yellow-400/60 shadow-[0_2px_14px_-4px_rgba(212,175,55,0.45)] ${className}`}
          title="Click to learn how Fast by NoFee works"
        >
          <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400" aria-hidden="true" />
          <span className="text-[12.5px] font-black uppercase tracking-[0.16em]">{BADGE_LABEL}</span>
        </button>
        {openModalIfNeeded}
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <button
          type="button"
          onClick={open_modal}
          data-testid={`${testid}-inline`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] bg-zinc-900/85 text-white border-yellow-400/45 backdrop-blur transition-transform hover:scale-[1.04] ${className}`}
          title={`${BADGE_LABEL} — click to learn more`}
        >
          <Zap className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" aria-hidden="true" />
          {BADGE_LABEL}
        </button>
        {openModalIfNeeded}
      </>
    );
  }

  if (variant === 'icon') {
    return (
      <>
        <button
          type="button"
          onClick={open_modal}
          data-testid={`${testid}-icon`}
          aria-label={`${BADGE_LABEL} — click to learn more`}
          className={`inline-flex items-center justify-center rounded-full border w-7 h-7 bg-zinc-900/85 border-yellow-400/45 backdrop-blur transition-transform hover:scale-[1.08] ${className}`}
          title={`${BADGE_LABEL} — click to learn more`}
        >
          <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" aria-hidden="true" />
        </button>
        {openModalIfNeeded}
      </>
    );
  }

  // "card" variant — compact lockup for restaurant directory cards.
  return (
    <>
      <button
        type="button"
        onClick={open_modal}
        data-testid={`${testid}-card`}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 bg-zinc-900/90 text-white border-yellow-400/45 backdrop-blur transition-transform hover:scale-[1.06] shadow-[0_2px_10px_-4px_rgba(212,175,55,0.45)] ${className}`}
        title={`${BADGE_LABEL} — click to learn more`}
      >
        <Zap className="w-3 h-3 text-yellow-400 fill-yellow-400" aria-hidden="true" />
        <span className="text-[10.5px] font-black uppercase tracking-[0.14em] leading-none">{BADGE_LABEL}</span>
      </button>
      {openModalIfNeeded}
    </>
  );
};

export default FastByNoFeeBadge;
